/** Lifted draft for the ruleset editor — a slim sibling of
 *  `useNotificationDraft`: sparse meta keys + the COMPLETE rules list when any
 *  rule changed (the backend reconciles by id and deletes omitted rows). */
import { useCallback, useMemo, useState } from 'react';

import type { RulesetDetail, RulesetWrite } from '@/api/notificationRulesets';

import { draftsFromDetail, rulesToWrite } from './rules/ruleSerialize';
import type { RuleDraft } from './rules/ruleTypes';

export interface RulesetDraftState {
  name: string;
  codename: string;
  match_type: number;
  rules: RuleDraft[];
}

export interface RulesetDraft {
  draft: RulesetDraftState;
  dirtyPaths: ReadonlySet<string>;
  dirty: boolean;
  setMeta: (key: 'name' | 'codename' | 'match_type', value: string | number) => void;
  setRules: (rules: RuleDraft[]) => void;
  buildPatch: (extra?: Pick<RulesetWrite, 'expected_updated_ts'>) => RulesetWrite;
  reset: (detail: RulesetDetail) => void;
}

function initialState(detail: RulesetDetail): RulesetDraftState {
  return {
    name: detail.name,
    codename: detail.codename ?? '',
    match_type: detail.match_type,
    rules: draftsFromDetail(detail.rules, detail.refs),
  };
}

export function useRulesetDraft(detail: RulesetDetail | undefined): RulesetDraft {
  const [state, setState] = useState<RulesetDraftState | null>(null);
  const [dirtyPaths, setDirtyPaths] = useState<Set<string>>(new Set());

  // Lazy init once the detail arrives (guarded render-phase state adjustment).
  if (state === null && detail) {
    setState(initialState(detail));
  }

  const markDirty = useCallback((path: string) => {
    setDirtyPaths((prev) => {
      if (prev.has(path)) return prev;
      const next = new Set(prev);
      next.add(path);
      return next;
    });
  }, []);

  const setMeta = useCallback<RulesetDraft['setMeta']>(
    (key, value) => {
      setState((s) => (s ? { ...s, [key]: value } : s));
      markDirty(key);
    },
    [markDirty],
  );

  const setRules = useCallback<RulesetDraft['setRules']>(
    (rules) => {
      setState((s) => (s ? { ...s, rules } : s));
      markDirty('rules');
    },
    [markDirty],
  );

  const buildPatch = useCallback<RulesetDraft['buildPatch']>(
    (extra) => {
      if (!state) return { ...extra };
      const body: RulesetWrite = { ...extra };
      if (dirtyPaths.has('name')) body.name = state.name;
      if (dirtyPaths.has('codename')) body.codename = state.codename.trim() || null;
      if (dirtyPaths.has('match_type')) body.match_type = state.match_type;
      if (dirtyPaths.has('rules')) body.rules = rulesToWrite(state.rules);
      return body;
    },
    [state, dirtyPaths],
  );

  const reset = useCallback((freshDetail: RulesetDetail) => {
    setState(initialState(freshDetail));
    setDirtyPaths(new Set());
  }, []);

  const draft = useMemo<RulesetDraftState>(
    () => state ?? { name: '', codename: '', match_type: 0, rules: [] },
    [state],
  );

  return {
    draft,
    dirtyPaths,
    dirty: dirtyPaths.size > 0,
    setMeta,
    setRules,
    buildPatch,
    reset,
  };
}
